import { FormattedNumber } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

import { toAmount } from '@/utils/to-amount'

import { isAbsent, type MoneyValue } from './ledger-money'

type MoneyCellProps = {
  value: MoneyValue
  /** Identifies the surface in Sentry when the value is present but non-numeric. */
  logPrefix: string
  /**
   * Open balance is the number a payout run is built from, so a positive amount
   * is accented; the other columns only mute a zero.
   */
  highlightPositive?: boolean
}

export const MoneyCell = ({ value, logPrefix, highlightPositive = false }: MoneyCellProps) => {
  if (isAbsent(value)) {
    // The same "no value" dash the method column uses. The breach reaches Sentry
    // via `useReportMissingLedgerMoney` — reporting here would fire once per row.
    return (
      <Typography
        color="text.disabled"
        variant="body2"
      >
        —
      </Typography>
    )
  }

  const amount = toAmount(value, logPrefix)

  return (
    <Typography
      variant="body2"
      className={cn(
        'tabular-nums',
        highlightPositive
          ? cn('font-medium', amount > 0 ? 'text-primary-main' : 'font-normal text-text-disabled')
          : amount === 0 && 'text-text-disabled',
      )}
    >
      <FormattedNumber
        currency="USD"
        maximumFractionDigits={2}
        minimumFractionDigits={2}
        style="currency"
        value={amount}
      />
    </Typography>
  )
}
